import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { LineSpinner } from 'ldrs/react';
import 'ldrs/react/LineSpinner.css';
import { useEffect, useRef, useState } from 'react';

const SHOW_DELAY_MS = 250;
const MIN_VISIBLE_MS = 600;

export function GlobalLoader() {
  const fetchingCount = useIsFetching();
  const mutatingCount = useIsMutating();
  const isActive = fetchingCount > 0 || mutatingCount > 0;

  const [visible, setVisible] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownAtRef = useRef<number | null>(null);

  // react-doctor-disable-next-line react-doctor/effect-needs-cleanup -- hand-managed timer state machine; unmount cleanup lives in the separate effect below (clears both timers)
  useEffect(() => {
    if (isActive) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (!visible && !showTimerRef.current) {
        showTimerRef.current = setTimeout(() => {
          shownAtRef.current = Date.now();
          setVisible(true);
          showTimerRef.current = null;
        }, SHOW_DELAY_MS);
      }
      return;
    }

    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }

    if (visible && !hideTimerRef.current) {
      const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : MIN_VISIBLE_MS;
      const remaining = Math.max(MIN_VISIBLE_MS - elapsed, 0);
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
        shownAtRef.current = null;
        hideTimerRef.current = null;
      }, remaining);
    }
  }, [isActive, visible]);

  // react-doctor-disable-next-line react-doctor/exhaustive-deps -- stable refs/functions; including them would not change behavior
  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  return (
    // react-doctor-disable-next-line react-doctor/prefer-tag-over-role -- full-screen status overlay; no native HTML tag fits, aria-live + aria-label announce it
    <div
      data-testid="global-loader"
      data-state={visible ? 'visible' : 'hidden'}
      role="status"
      aria-live="polite"
      aria-label="Cargando"
      aria-hidden={!visible}
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-background/40 backdrop-blur-[1px] pointer-events-none transition-opacity duration-200 ease-out ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <LineSpinner size="48" stroke="3" speed="0.8" color="currentColor" />
    </div>
  );
}
